import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Trash2, Loader2, ArrowLeft } from "lucide-react";
import SEO from "@/components/SEO";
import {
  adminCommentAction,
  adminListComments,
  adminMe,
  getAdminToken,
  setAdminToken,
} from "@/lib/api";
import { useSessionRenewal } from "@/hooks/useSessionRenewal";

const TABS = [
  { key: "Pending", label: "Pending" },
  { key: "Approved", label: "Approved" },
  { key: "Rejected", label: "Rejected" },
];

export default function AdminComments() {
  const nav = useNavigate();
  const [tab, setTab] = useState("Pending");
  const [comments, setComments] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  useEffect(() => {
    if (!getAdminToken()) { nav("/admin/login", { replace: true }); return; }
    adminMe()
      .then((res) => { if (res?.expires_at) setAdminToken(getAdminToken(), res.expires_at); })
      .catch(() => { setAdminToken(""); nav("/admin/login", { replace: true }); });
  }, [nav]);

  useSessionRenewal({ onExpired: () => { setAdminToken(""); nav("/admin/login", { replace: true }); } });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await adminListComments(tab);
      setComments(d.comments || []);
      setCounts(d.counts || {});
    } catch {
      toast.error("Could not load comments.");
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    if (action === "delete" && !window.confirm("Delete this comment permanently?")) return;
    setBusy((b) => ({ ...b, [id]: action }));
    try {
      await adminCommentAction(id, action);
      toast.success(action === "delete" ? "Deleted." : `${action[0].toUpperCase()}${action.slice(1)}d.`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed.");
    } finally {
      setBusy((b) => ({ ...b, [id]: null }));
    }
  };

  return (
    <main data-testid="admin-comments-page" className="bg-white min-h-screen">
      <SEO title="Comments Moderation" path="/admin/comments" noIndex />

      <section className="border-b border-[#e5e7eb] bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <span className="ash-eyebrow">Ashlan Admin</span>
            <h1 className="text-2xl md:text-3xl font-bold text-[#080808]">Comments Moderation</h1>
          </div>
          <Link to="/admin/blog" className="text-sm text-[#3A7A8C] hover:underline inline-flex items-center gap-1.5">
            <ArrowLeft size={14} /> Blog console
          </Link>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <div className="flex gap-2 border-b border-[#e5e7eb] mb-6" data-testid="tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key.toLowerCase()}`}
              className={`px-4 py-3 text-sm font-semibold transition-colors ${
                tab === t.key
                  ? "text-[#3A7A8C] border-b-2 border-[#3A7A8C] -mb-px"
                  : "text-[#5A5F6A] hover:text-[#080808]"
              }`}
            >
              {t.label} <span className="text-xs opacity-60">({counts[t.key] || 0})</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center text-[#5A5F6A] py-12"><Loader2 className="animate-spin mr-2" /> Loading…</div>
        ) : comments.length === 0 ? (
          <div className="border border-dashed border-[#e5e7eb] p-12 text-center text-[#5A5F6A]" data-testid="comments-empty">
            No {tab.toLowerCase()} comments.
          </div>
        ) : (
          <div className="space-y-4" data-testid="comments-list">
            {comments.map((c) => (
              <div key={c.id} className="border border-[#e5e7eb] bg-white p-5" data-testid={`comment-${c.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold text-[#080808]">{c.author_name}</div>
                    <div className="text-xs text-[#5A5F6A] mt-0.5">
                      {c.author_email} · {c.created_at?.slice(0, 16).replace("T", " ")}
                    </div>
                    <div className="text-xs text-[#3A7A8C] mt-1">
                      On: <a href={`/blog/${c.post_slug}`} target="_blank" rel="noreferrer" className="underline">{c.post_title}</a>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {tab !== "Approved" && (
                      <button
                        onClick={() => act(c.id, "approve")}
                        disabled={busy[c.id]}
                        className="ash-btn-outline inline-flex items-center gap-1.5 text-xs px-3 py-2 disabled:opacity-50"
                        data-testid={`approve-${c.id}`}
                      >
                        {busy[c.id] === "approve" ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                        Approve
                      </button>
                    )}
                    {tab !== "Rejected" && (
                      <button
                        onClick={() => act(c.id, "reject")}
                        disabled={busy[c.id]}
                        className="ash-btn-outline inline-flex items-center gap-1.5 text-xs px-3 py-2 disabled:opacity-50"
                        data-testid={`reject-${c.id}`}
                      >
                        {busy[c.id] === "reject" ? <Loader2 size={12} className="animate-spin" /> : <XCircle size={12} />}
                        Reject
                      </button>
                    )}
                    <button
                      onClick={() => act(c.id, "delete")}
                      disabled={busy[c.id]}
                      className="text-xs text-rose-700 hover:text-rose-900 inline-flex items-center gap-1.5 px-3 py-2 disabled:opacity-50"
                      data-testid={`delete-${c.id}`}
                    >
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>
                </div>
                <p className="mt-3 text-sm text-[#3C4048] whitespace-pre-wrap leading-relaxed">{c.text}</p>
              </div>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
